import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const advancedGroupsCode = `// 1. Named Capture Groups (?<name>...)
// Improves readability and maintainability
const dateStr = "2024-12-31";
const dateRegex = /(?<year>\\d{4})-(?<month>\\d{2})-(?<day>\\d{2})/;
const { groups: { year, month, day } } = dateStr.match(dateRegex);

// 2. Non-Capturing Groups (?:...)
// Matches but doesn't "remember", saving memory/performance
const protocolRegex = /(?:https?|ftp):\\/\\/[^\\s/$.?#].[^\\s]*/i;

// 3. Backreferences (\\1)
// Matches repeated sequences
const duplicateWordRegex = /\\b(\\w+)\\s+\\1\\b/gi;
const sentence = "The the cat sat sat on on the mat.";
console.log(sentence.match(duplicateWordRegex)); // ["The the", "sat sat", "on on"]`;

const lookaroundPatternsCode = `// Advanced Lookarounds (Zero-Width Assertions)

// 1. Positive Lookahead (?=)
// Match string only if followed by certain chars
const passwordRegex = /^(?=.*[A-Z])(?=.*\\d).{8,}$/; 
// At least one capital, one digit, min 8 chars

// 2. Positive Lookbehind (?<=) - ES2018+
// Match digits ONLY if preceded by '$'
const priceText = "Item 1: $49, Item 2: 50";
const usdPrices = priceText.match(/(?<=\\$)\\d+/g); // ["49"]

// 3. Negative Lookahead (?!)
// Match 'user' only if NOT followed by '@' (i.e. not an email)
const usernameRegex = /user(?!@)/g;`;

const JSRegexPatterns = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Pattern Engineering</div>
                <h1>Advanced Regex Patterns</h1>
                <p className="hero-lead">
                    Move beyond simple matches. Master zero-width assertions, named
                    capture groups, and backreferences to solve complex text-parsing
                    challenges with precision and performance.
                </p>
            </div>

            <div className="content-section">
                <h2>Logical Grouping & Named Captures</h2>
                <p>
                    Groups `(...)` allow you to treat multiple characters as a single
                    unit for quantifiers. In modern JavaScript (ES2018+), **Named
                    Capture Groups** allow you to access matched fragments via a
                    descriptive `groups` object rather than fragile array indices.
                    Furthermore, **Non-Capturing Groups** `(?:...)` should be used
                    when you need logical grouping but don't intend to extract the
                    data, reducing the memory footprint of the matching operation.
                </p>
                <CodeBlock code={advancedGroupsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Lookarounds: Matching Context</h2>
                <p>
                    Lookarounds are **Zero-Width Assertions**. They match a position
                    based on the surrounding context (lookahead `(?=)` or
                    lookbehind `(?&lt;=)`) without consuming the characters themselves.
                    They are essential for tasks like "find the number, but only if
                    it represents a dollar amount," or for multi-condition
                    validations like complex password requirements.
                </p>
                <CodeBlock code={lookaroundPatternsCode} language="javascript" />
            </div>

            <ProTip><p>
                    While Positive Lookahead has been supported for decades,
                    **Lookbehind** (both positive `(?&lt;=)` and negative `(?&lt;!)`)
                    only arrived in ES2018. Before this, developers had to use
                    clunky string reversals or manual substring checks. Lookbehinds
                    must have fixed widths in some engines, though JavaScript's
                    V8 engine supports variable-width lookbehinds, making it
                    one of the most powerful regex engines in the browser ecosystem.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Legibility:** Use Named Groups `(?&lt;name&gt;)` for complex extractions.</li>
                    <li>**Efficiency:** Use Non-Capturing Groups `(?:)` if you don't need to extract the data.</li>
                    <li>**Context:** Use Lookarounds to match positions relative to anchors without consuming text.</li>
                    <li>**Repetition:** Use Backreferences `\1` to identify repeated patterns like double words.</li>
                    <li>**Unicode:** Always use the `u` flag when grouping characters from non-Latin scripts.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSRegexPatterns;


